/** The upstream-change review queue (plan §3.3.6): products whose AI-built outputs changed
 *  upstream under the "raise with ops" policy, products removed from the listing, and applies
 *  that could not start. Per product: what changed ("built from" vs "now"), the outputs waiting,
 *  a dry-run preview, and apply / dismiss (with a note) / decouple (with a reason). Every action
 *  is audited server-side.
 *
 *  Apply starts the standard product sync; when it cannot start (409 `{started: false, code,
 *  detail}`) the change stays pending — the card says so, with the reason. */
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  Pagination,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import * as catalogueApi from '@/api/catalogue';
import {
  catalogueKeys,
  type ApplyResult,
  type CataloguePreview,
  type CatalogueScope,
  type ReviewEntry,
} from '@/api/catalogue';
import { AppDialog } from '@/components/AppDialog';
import { useSnackbar } from '@/components/Snackbar';
import { openBackgroundTask } from '@/features/bgtasks/openBackgroundTask';
import { formatDate, formatRelativeTime } from '@/utils/format';

import { entityLabel } from './catalogueModel';
import { PreviewResult } from './PreviewResult';
import { useInvalidateCatalogue } from './queries';

const MONO = 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace';
const PAGE_SIZE = 20;
const DIFF_ROWS = 6;

function short(v: unknown): string {
  if (v === null || v === undefined) return '—';
  const text = typeof v === 'string' ? v : JSON.stringify(v);
  return text.length > 160 ? `${text.slice(0, 160)}…` : text;
}

type Action = 'apply' | 'dismiss' | 'decouple';

const ACTION_COPY: Record<Action, { title: string; body: string; button: string; noteLabel: string; required: boolean }> = {
  apply: {
    title: 'Apply the change',
    body: 'Starts a product sync for this product now: its coupled outputs are rebuilt from the new upstream content, overwriting edits made by hand. Decoupled outputs stay as they are.',
    button: 'Apply',
    noteLabel: 'Note (optional)',
    required: false,
  },
  dismiss: {
    title: 'Dismiss the change',
    body: 'Accepts the new upstream content without rebuilding: the outputs stay as they are, and later changes are judged against the new content.',
    button: 'Dismiss',
    noteLabel: 'Note (optional)',
    required: false,
  },
  decouple: {
    title: 'Decouple the outputs in review',
    body: 'Leaves the outputs waiting here out of every later build until they are re-coupled. The change is then resolved.',
    button: 'Decouple',
    noteLabel: 'Reason (required)',
    required: true,
  },
};

function ReviewCard({
  scope,
  entry,
  previewSupported,
  onOpenProduct,
}: {
  scope: CatalogueScope;
  entry: ReviewEntry;
  previewSupported: boolean;
  onOpenProduct: (productId: number) => void;
}) {
  const { show } = useSnackbar();
  const invalidate = useInvalidateCatalogue(scope.tenantId);
  const [allDiff, setAllDiff] = useState(false);
  const [action, setAction] = useState<Action | null>(null);
  const [note, setNote] = useState('');
  const [blocked, setBlocked] = useState<ApplyResult | null>(null);
  const [preview, setPreview] = useState<CataloguePreview | null>(null);
  const [previewNote, setPreviewNote] = useState<string | null>(null);
  const pending = entry.pending_change;
  const name = entry.title || entry.name;
  const diff = entry.field_diff;
  const inReview = entry.in_review;
  const apply = pending?.apply;

  const previewMut = useMutation({
    mutationFn: () => catalogueApi.reviewPreview(scope.tenantId, entry.id),
    onMutate: () => {
      setPreview(null);
      setPreviewNote(null);
    },
    onSuccess: setPreview,
    onError: (e) =>
      setPreviewNote(
        catalogueApi.isPreviewUnsupported(e)
          ? 'This integration has no dry-run build yet, so there is no preview.'
          : (e as Error).message,
      ),
  });

  const actionMut = useMutation({
    mutationFn: async (a: Action) => {
      const n = note.trim();
      if (a === 'apply') return { a, r: await catalogueApi.applyChange(scope.tenantId, entry.id, n || undefined) };
      if (a === 'dismiss') return { a, r: await catalogueApi.dismissChange(scope.tenantId, entry.id, n || undefined) };
      return { a, r: await catalogueApi.decoupleInReview(scope.tenantId, entry.id, n) };
    },
    onMutate: () => setBlocked(null),
    onSuccess: async ({ a, r }) => {
      setAction(null);
      setNote('');
      if (a === 'apply') {
        const started = r as ApplyResult;
        show('Sync started — the change is applied when it completes.', 'success', {
          autoHideDuration: 15000,
          ...(started.record_id
            ? { action: { label: 'View task', onClick: () => openBackgroundTask(started.record_id!) } }
            : {}),
        });
      } else {
        show(a === 'dismiss' ? 'Change dismissed.' : 'Outputs decoupled.', 'success');
      }
      await invalidate();
    },
    onError: async (e) => {
      const refusal = catalogueApi.applyRefusalOf(e);
      if (refusal) {
        setAction(null);
        setBlocked(refusal);
        await invalidate();
      }
    },
  });

  const copy = action ? ACTION_COPY[action] : null;
  return (
    <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5 }} data-testid={`review-${entry.id}`} aria-label={`Review: ${name}`} component="section">
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, flexWrap: 'wrap' }}>
        <Box sx={{ flex: 1, minWidth: 240 }}>
          <Button
            variant="text"
            onClick={() => onOpenProduct(entry.id)}
            sx={{ p: 0, minWidth: 0, textTransform: 'none', fontWeight: 600, fontSize: 15 }}
          >
            {name}
          </Button>
          <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center', flexWrap: 'wrap' }}>
            <Typography variant="caption" sx={{ fontFamily: MONO, color: 'text.secondary' }}>
              {entry.vendor_id}
            </Typography>
            {pending?.removed && <Chip size="small" label="removed from the listing" color="error" variant="outlined" sx={{ height: 20, fontSize: 11 }} />}
            {(pending?.categories ?? []).map((c) => (
              <Chip key={c} size="small" label={c.replace(/_/g, ' ')} variant="outlined" sx={{ height: 20, fontSize: 11 }} />
            ))}
            {pending?.detected_ts && (
              <Typography variant="caption" color="text.secondary" title={formatDate(pending.detected_ts)}>
                detected {formatRelativeTime(pending.detected_ts)}
              </Typography>
            )}
          </Box>
          {pending?.summary && (
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              {pending.summary}
            </Typography>
          )}
        </Box>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Tooltip
            title={previewSupported ? 'Dry-run re-plan of the outputs in review — nothing is written' : 'This integration has no dry-run build yet'}
          >
            <span>
              <Button size="small" variant="outlined" disabled={!previewSupported || previewMut.isPending} onClick={() => previewMut.mutate()}>
                Preview
              </Button>
            </span>
          </Tooltip>
          <Button size="small" variant="contained" onClick={() => setAction('apply')} disabled={actionMut.isPending}>
            Apply…
          </Button>
          <Button size="small" onClick={() => setAction('dismiss')} disabled={actionMut.isPending}>
            Dismiss…
          </Button>
          <Button size="small" color="warning" onClick={() => setAction('decouple')} disabled={actionMut.isPending || inReview.length === 0}>
            Decouple…
          </Button>
        </Stack>
      </Box>

      {(blocked || apply?.state === 'blocked' || apply?.state === 'failed') && (
        <Alert severity="warning" sx={{ mt: 1 }}>
          <AlertTitle>The sync could not start — the change stays pending</AlertTitle>
          {blocked
            ? `${blocked.detail ?? ''}${blocked.code ? ` (${blocked.code})` : ''}`
            : `${apply?.note ?? ''}${apply?.code ? ` (${apply.code})` : ''}${apply?.ts ? ` · ${formatDate(apply.ts)}` : ''}`}
          {apply?.bgtask && (
            <>
              {' '}
              <RouterLink to={`/bgtasks/${apply.bgtask}`}>task</RouterLink>
            </>
          )}
        </Alert>
      )}

      {inReview.length > 0 && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Waiting for ops
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
            {inReview.map((o) => (
              <Tooltip key={o.id} describeChild title={`${o.reason ?? ''}${o.ai_built ? ' · AI-built' : ''}`}>
                <Chip
                  size="small"
                  label={`${entityLabel(o.entity ?? '')} ${o.ext_id}${o.option_id ? ` · ${o.option_id}` : ''}`}
                  variant="outlined"
                  sx={{ height: 22, fontSize: 11 }}
                />
              </Tooltip>
            ))}
          </Box>
        </Box>
      )}

      {diff.length > 0 && (
        <Box sx={{ mt: 1, overflowX: 'auto' }}>
          <Table size="small" aria-label={`What changed: ${name}`}>
            <TableHead>
              <TableRow>
                <TableCell>Field</TableCell>
                <TableCell>Built from</TableCell>
                <TableCell>Now</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(allDiff ? diff : diff.slice(0, DIFF_ROWS)).map((d, i) => (
                <TableRow key={`${d.path}-${i}`}>
                  <TableCell sx={{ verticalAlign: 'top' }}>
                    <Typography variant="caption" sx={{ fontFamily: MONO, wordBreak: 'break-all' }}>
                      {d.path}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {d.category} · {d.part}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ verticalAlign: 'top', color: 'text.secondary', maxWidth: 320 }} title={typeof d.before === 'string' ? d.before : undefined}>
                    {short(d.before)}
                  </TableCell>
                  <TableCell sx={{ verticalAlign: 'top', maxWidth: 320 }} title={typeof d.after === 'string' ? d.after : undefined}>
                    {short(d.after)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {diff.length > DIFF_ROWS && (
            <Button size="small" onClick={() => setAllDiff((v) => !v)}>
              {allDiff ? 'Show fewer' : `Show all ${diff.length} differences`}
            </Button>
          )}
        </Box>
      )}

      {(previewMut.isPending || preview || previewNote) && (
        <Box sx={{ mt: 1.5 }} aria-live="polite">
          {previewMut.isPending && <CircularProgress size={18} aria-label="Building the preview" />}
          {previewNote && (
            <Alert severity="info" sx={{ py: 0 }}>
              {previewNote}
            </Alert>
          )}
          {preview && <PreviewResult preview={preview} />}
        </Box>
      )}

      {action && copy && (
        <AppDialog
          open
          onClose={() => setAction(null)}
          title={`${copy.title}: ${name}`}
          actions={
            <>
              <Button onClick={() => setAction(null)}>Cancel</Button>
              <Button
                variant="contained"
                color={action === 'decouple' ? 'warning' : 'primary'}
                disabled={actionMut.isPending || (copy.required && !note.trim())}
                onClick={() => actionMut.mutate(action)}
              >
                {copy.button}
              </Button>
            </>
          }
        >
          <Typography variant="body2" sx={{ mb: 2 }}>
            {copy.body}
          </Typography>
          <TextField
            label={copy.noteLabel}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            fullWidth
            multiline
            minRows={2}
            size="small"
            inputProps={{ maxLength: 500 }}
          />
          {actionMut.isError && !catalogueApi.applyRefusalOf(actionMut.error) && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {(actionMut.error as Error).message}
            </Alert>
          )}
        </AppDialog>
      )}
    </Paper>
  );
}

export function ReviewQueue({
  scope,
  previewSupported,
  onOpenProduct,
}: {
  scope: CatalogueScope;
  previewSupported: boolean;
  onOpenProduct: (productId: number) => void;
}) {
  const [page, setPage] = useState(1);
  const query = useQuery({
    queryKey: catalogueKeys.review(scope, { page, page_size: PAGE_SIZE }),
    queryFn: ({ signal }) => catalogueApi.listReview(scope, { page, page_size: PAGE_SIZE }, signal),
    placeholderData: keepPreviousData,
    retry: false,
  });
  if (query.isLoading) return <CircularProgress size={24} aria-label="Loading the review queue" />;
  if (query.isError) return <Alert severity="error">{(query.error as Error).message}</Alert>;
  const results = query.data?.results ?? [];
  const count = query.data?.count ?? 0;
  if (!results.length) {
    return (
      <Alert severity="success" variant="outlined">
        Nothing waits for review. Under the &ldquo;Raise with ops&rdquo; policy, products whose AI-built outputs change
        upstream wait here; so do products removed from the listing and applies that could not start.
      </Alert>
    );
  }
  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {count} product{count === 1 ? '' : 's'} waiting · most recently detected first
      </Typography>
      {results.map((entry) => (
        <ReviewCard key={entry.id} scope={scope} entry={entry} previewSupported={previewSupported} onOpenProduct={onOpenProduct} />
      ))}
      {count > PAGE_SIZE && (
        <Pagination count={Math.ceil(count / PAGE_SIZE)} page={page} onChange={(_, p) => setPage(p)} size="small" />
      )}
    </Box>
  );
}
